import { WarningCircle } from "@phosphor-icons/react";
import { type ReactNode, useId, useLayoutEffect, useRef, useState } from "react";
import { cx } from "@/lib/cx";

/**
 * A value edited where it is shown. No edit mode: it looks like text until hovered or focused, saves on Enter or
 * blur (Shift+Enter breaks a line in the multiline variant), Escape restores the saved value, and focus stays in
 * the field after saving or cancelling. `onSave` may reject: the draft is kept and `errorMessage` says why, next to
 * the field (announced as an alert).
 *
 * Hints (live validation, "A becomes B" previews, errors) float under the field instead of pushing the layout, so a
 * header with tabs below it never jumps while editing.
 */
export interface InlineEditProps {
  value: string;
  /** Accessible name; there is no visible label. */
  label: string;
  onSave: (next: string) => Promise<void>;
  /** Text for a rejected save (default: generic). */
  errorMessage?: (error: unknown) => string;
  multiline?: boolean;
  placeholder?: string;
  /** Applied to what the user types (uppercasing, stripping characters). */
  normalize?: (raw: string) => string;
  /** Live check of the draft: a message blocks saving and shows under the field. */
  validate?: (draft: string) => string | null;
  /** Shown under the field while the draft is valid and differs from the saved value. */
  preview?: (draft: string) => ReactNode;
  /** An empty draft saves as "" instead of being treated as a cancel. */
  allowEmpty?: boolean;
  maxLength?: number;
  /** Typography and spacing of the text (size, weight, colour). */
  className?: string;
  spellCheck?: boolean;
  lang?: string;
  /** Focus the field when it first renders (a page that was replaced after a save puts the user back where they were). */
  autoFocus?: boolean;
}

const base =
  "block rounded-control border border-transparent bg-transparent px-1.5 transition-ui " +
  "placeholder:text-fg-subtle hover:bg-hover focus:border-input-border focus:bg-input-bg read-only:opacity-70";

export function InlineEdit({
  value,
  label,
  onSave,
  errorMessage,
  multiline = false,
  placeholder,
  normalize,
  validate,
  preview,
  allowEmpty = false,
  maxLength,
  className,
  spellCheck,
  lang,
  autoFocus,
}: InlineEditProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const hintId = useId();
  const text = draft ?? value;

  // Multiline: the textarea is exactly as tall as its text.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the text changes
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${el.scrollHeight}px`;
  }, [text, multiline]);

  const clean = (raw: string) => (multiline ? raw.trim() : raw.replace(/\s+/g, " ").trim());
  const dirty = draft !== null && clean(draft) !== value;
  const problem = draft !== null ? (validate?.(clean(draft)) ?? null) : null;
  const reset = () => {
    setDraft(null);
    setError(null);
  };

  const commit = async (fromBlur: boolean) => {
    if (draft === null || saving) return;
    const next = clean(draft);
    if (next === value || (next === "" && !allowEmpty)) return reset();
    if (validate?.(next)) {
      // Enter on an invalid draft keeps it (the hint says why); leaving the field drops it.
      if (fromBlur) reset();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      reset();
    } catch (e) {
      setError(errorMessage?.(e) ?? null);
      setDraft(next);
    } finally {
      setSaving(false);
    }
  };

  const message = error ?? problem;
  const shown = message ? "error" : dirty && preview ? "preview" : null;
  const previewNode = shown === "preview" && draft !== null ? preview?.(clean(draft)) : null;

  const field = {
    "aria-label": label,
    "aria-invalid": message ? true : undefined,
    "aria-describedby": hintId,
    lang,
    spellCheck,
    autoFocus,
    placeholder,
    maxLength,
    readOnly: saving,
    value: text,
    onFocus: () => setFocused(true),
    onBlur: () => {
      setFocused(false);
      void commit(true);
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.nativeEvent.isComposing) return;
      if (e.key === "Enter" && !(multiline && e.shiftKey)) {
        e.preventDefault();
        void commit(false);
      } else if (e.key === "Escape" && (draft !== null || error)) {
        e.preventDefault();
        e.stopPropagation();
        reset();
      }
    },
  };
  const onChange = (raw: string) => {
    setError(null);
    setDraft(normalize ? normalize(raw) : raw);
  };

  return (
    <span className={cx("relative -mx-1.5 block min-w-0", multiline ? "w-[calc(100%+0.75rem)]" : "max-w-full")}>
      {multiline ? (
        <textarea
          ref={area}
          rows={1}
          {...field}
          onChange={(e) => onChange(e.target.value)}
          className={cx(base, "w-full min-w-0 resize-none overflow-hidden py-0.5", className)}
        />
      ) : (
        // The invisible copy of the text sizes the cell, so the field is as wide as its text (up to the space it has).
        <span className="inline-grid max-w-full overflow-hidden align-top">
          <span
            aria-hidden="true"
            className={cx("invisible whitespace-pre border border-transparent px-1.5 [grid-area:1/1]", className)}
          >
            {text || placeholder || " "}
            {" "}
          </span>
          <input
            type="text"
            {...field}
            onChange={(e) => onChange(e.target.value)}
            className={cx(base, "w-0 min-w-full truncate [grid-area:1/1]", className)}
          />
        </span>
      )}
      <span id={hintId} aria-live="polite" className="contents">
        {(focused || error) && shown ? (
          <span
            role={error ? "alert" : undefined}
            className={cx(
              "absolute top-full left-0 z-30 mt-1 flex w-max max-w-[min(30rem,calc(100vw-2.5rem))] items-start gap-1.5 rounded-control border px-2 py-1 text-xs font-normal tracking-normal shadow-md",
              shown === "error"
                ? "border-danger-soft-fg/30 bg-danger-soft text-danger-soft-fg"
                : "border-popover-border bg-popover-bg text-fg-muted",
            )}
          >
            {shown === "error" ? (
              <WarningCircle size={14} weight="fill" aria-hidden="true" className="mt-px shrink-0" />
            ) : null}
            <span className="min-w-0">{shown === "error" ? message : previewNode}</span>
          </span>
        ) : null}
      </span>
    </span>
  );
}
